import { v4 as uuidv4 } from "uuid";
import { ELEMENT_TYPES } from "../constants/boqElementTypes";

/**
 * boqAvaAdapter.js
 *
 * Bidirectional conversion between AVA Cloud ProjectDto and the BoQ editor's
 * internal tree format.
 *
 * WHY FULL-SAVE (not per-operation indices):
 *  - All edits happen in `rows` state via boqTreeUtils (add/remove/move/update).
 *  - On save, editorNodesToAva(rows, baseGaebData) rebuilds the complete
 *    `elements[]` array in traversal order – the array index is implicitly
 *    correct because `rows` already reflects the desired final order.
 *  - No per-operation index tracking is needed.
 *
 * AVA → Editor field mapping
 * ──────────────────────────────────────────────────────────────────────────
 * element.id                              → node.id
 * element.elementTypeDiscriminator        → node.type  (via AVA_DISC_TO_TYPE)
 * element.elements[]                      → node.children[]
 * element.itemNumber.stringRepresentation → node.itemNumber  (plain string)
 * element.shortText                       → node.shortText
 * element.longText                        → node.longText
 * element.quantityOverride ?? quantity    → node.quantity   (user override preferred)
 * element.priceComponents[0].values[0].formula ?? unitPrice  → node.unitPrice  (user-entered preferred)
 * quantityOverride ?? quantity                                 → node.quantity
 * element.unitTag                         → node.unitTag
 * element.taxRate                         → node.taxRate  (decimal, same as AVA)
 * (original element kept as node._ava)    → lossless reverse for all other fields
 *
 * On write (editor → AVA):
 * node.quantity   → base.quantityOverride  (AVA-authoritative user value)
 * node.unitPrice  → base.priceComponents[0].values[0].formula (AVA-authoritative user value)
 * node.taxRate → base.taxRate (decimal, no conversion) + base.useDifferentTaxRate
 *
 * Type resolution rules
 * ──────────────────────────────────────────────────────────────────────────
 * ServiceSpecificationGroupDto  at depth 0  →  "group"
 * ServiceSpecificationGroupDto  at depth ≥1 →  "subgroup"
 * PositionDto                               →  "position"
 * NoteTextDto + isOpeningText/isClosingText →  "additional_text"
 * NoteTextDto + addendumNumber="__exec__"   →  "execution_description"
 * NoteTextDto (default)                     →  "note"
 */

// ─── AVA discriminator constants ────────────────────────────────────────────
const DISC_GROUP = "ServiceSpecificationGroupDto";
const DISC_POSITION = "PositionDto";
const DISC_NOTE = "NoteTextDto";

// Editor type → AVA discriminator
const EDITOR_TO_DISC = {
	[ELEMENT_TYPES.GROUP]: DISC_GROUP,
	[ELEMENT_TYPES.SUBGROUP]: DISC_GROUP,
	[ELEMENT_TYPES.POSITION]: DISC_POSITION,
	[ELEMENT_TYPES.NOTE]: DISC_NOTE,
	[ELEMENT_TYPES.ADDITIONAL_TEXT]: DISC_NOTE,
	[ELEMENT_TYPES.EXECUTION_DESCRIPTION]: DISC_NOTE,
};

/** Marker stored in addendumNumber to identify execution_description nodes. */
const EXEC_DESC_MARKER = "__exec_desc__";

/**
 * Standard GAEB item number schema injected into AVA ProjectDto payloads when the
 * original service specification does not already define one.
 * Format: GG.SS.PPPP[.I]  (Group 2 + Subgroup 2 + Position 4 + Index 1 = 9 total chars)
 * Exported so the editor page can initialise schema state from this default.
 */
export const DEFAULT_GAEB_ITEM_NUMBER_SCHEMA = {
	tiers: [
		{
			type: "Numeric",
			isLot: false,
			length: 2,
			tierType: "Group",
			increment: 1,
		},
		{
			type: "Numeric",
			isLot: false,
			length: 2,
			tierType: "Group",
			increment: 1,
		},
		{
			type: "Numeric",
			isLot: false,
			length: 4,
			tierType: "Position",
			increment: 10,
		},
		{
			type: "Alphanumeric",
			isLot: false,
			length: 1,
			tierType: "Index",
			increment: 1,
		},
	],
	filler: "0",
	separator: ".",
	totalLength: 9,
	skippedTiersFiller: "_",
	allowUpperCaseLettering: false,
	schemaIsCorrectlyDefined: true,
	disableLeftPaddingTiersWithFiller: false,
	skipNonExistingLevelsInPositionItemNumbers: false,
};

// ─── AVA → Editor ────────────────────────────────────────────────────────────

/**
 * Determine the editor type for a single AVA element.
 * @param {Object} element  - AVA element object
 * @param {number} depth    - 0 = root of serviceSpecification, 1+ = nested
 * @returns {string}        - editor type string
 */
function discriminatorToEditorType(element, depth) {
	const disc = element.elementTypeDiscriminator ?? element.elementType;

	if (disc === DISC_GROUP) {
		return depth === 0 ? ELEMENT_TYPES.GROUP : ELEMENT_TYPES.SUBGROUP;
	}
	if (disc === DISC_POSITION) {
		return ELEMENT_TYPES.POSITION;
	}
	if (disc === DISC_NOTE) {
		if (element.addendumNumber === EXEC_DESC_MARKER)
			return ELEMENT_TYPES.EXECUTION_DESCRIPTION;
		if (element.isOpeningText || element.isClosingText)
			return ELEMENT_TYPES.ADDITIONAL_TEXT;
		return ELEMENT_TYPES.NOTE;
	}
	// Unknown discriminator → treat as note
	return ELEMENT_TYPES.NOTE;
}

/**
 * Convert one AVA element to an editor node (recursive).
 * @param {Object} element - AVA element
 * @param {number} depth   - nesting depth (0 = root)
 * @returns {Object}       - editor node
 */
function avaElementToEditorNode(element, depth = 0) {
	const type = discriminatorToEditorType(element, depth);

	const node = {
		id: element.id ?? uuidv4(),
		type,
		itemNumber: element.itemNumber?.stringRepresentation ?? null,
		shortText: element.shortText ?? "",
		longText: element.longText ?? "",
		// position-specific: prefer user-entered overrides over AVA-computed values.
		// For unitPrice: read from priceComponents[0].values[0].formula (our write format),
		// then fall back to unitPriceOverride (legacy) then AVA-computed unitPrice.
		quantity: element.quantityOverride ?? element.quantity ?? null,
		unitPrice:
			element.priceComponents?.[0]?.values?.[0]?.formula != null
				? parseFloat(element.priceComponents[0].values[0].formula)
				: (element.unitPriceOverride ?? element.unitPrice ?? null),
		unitTag: element.unitTag ?? "",
		// taxRate mirrors the AVA decimal field (e.g. 0.19 = 19%).
		// Only set when AVA says this position has its own rate; null = inherit project-level rate.
		taxRate: element.useDifferentTaxRate ? (element.taxRate ?? null) : null,
		// AVA-computed totals - read directly so the editor shows server-authoritative values.
		totalPrice: element.totalPrice ?? null,
		totalPriceGross: element.totalPriceGross ?? null,
		// preserve original AVA element so reverse conversion is lossless
		_ava: element,
	};

	// Recurse into children (AVA calls them `elements`, editor calls them `children`)
	const childElements = Array.isArray(element.elements)
		? element.elements
		: [];
	node.children = childElements.map((child) =>
		avaElementToEditorNode(child, depth + 1),
	);

	return node;
}

/**
 * Convert a full AVA ProjectDto into the editor's root `rows` array.
 *
 * @param {Object} gaebData - AVA ProjectDto (as returned by recalculateGaebData)
 * @returns {Array}         - editor rows (root nodes with children[])
 */
export function avaToEditorNodes(gaebData) {
	const elements = gaebData?.serviceSpecifications?.[0]?.elements ?? [];
	return elements.map((el) => avaElementToEditorNode(el, 0));
}

// ─── Editor → AVA ────────────────────────────────────────────────────────────

/**
 * Convert one editor node back to an AVA element (recursive).
 * - Starts from original _ava to preserve all AVA-specific metadata.
 * - Only overrides fields the editor actually manages.
 * - New nodes (no _ava) get a fresh UUID and minimal required fields.
 *
 * @param {Object} node - editor node
 * @returns {Object}    - AVA element
 */
function editorNodeToAvaElement(node) {
	const disc = EDITOR_TO_DISC[node.type] ?? DISC_NOTE;

	// Build a clean minimal element from scratch - intentionally NOT spreading node._ava.
	//
	// Spreading _ava would carry stale computed fields (totalPrice, totalPriceGross,
	// deductedPrice, quantityOverride from a prior save, etc.) back to AVA Cloud.
	//
	// We now include itemNumber so AVA Cloud preserves our locally-generated OZ numbers.
	// AVA's tryAutoGenerateItemNumbersAndSchema only fills *missing* itemNumbers, so
	// providing ours prevents AVA from overriding them after reorders/additions.
	const base = {
		id: node.id ?? uuidv4(),
		elementTypeDiscriminator: disc,
		elementType: disc, // some AVA versions read elementType instead
		shortText: node.shortText ?? "",
		longText: node.longText ?? "",
		// Pass our locally-generated OZ so AVA Cloud preserves it rather than regenerating.
		...(node.itemNumber != null && node.itemNumber !== ""
			? { itemNumber: { stringRepresentation: node.itemNumber } }
			: {}),
	};

	// Group / Subgroup: recurse children into elements[]
	if (
		node.type === ELEMENT_TYPES.GROUP ||
		node.type === ELEMENT_TYPES.SUBGROUP
	) {
		base.elements = (node.children ?? []).map(editorNodeToAvaElement);
	}

	// Position: only user-entered values - AVA computes totalPrice, gross, etc.
	// We use priceComponents (AVA Cloud standard) instead of unitPriceOverride so
	// that AVA correctly applies tax rate calculations when deriving totals.
	if (node.type === ELEMENT_TYPES.POSITION) {
		const qty = node.quantity ?? 0;
		const price = node.unitPrice ?? 0;
		base.quantityOverride = qty;
		// Set price via priceComponents so AVA Cloud recalculates tax correctly.
		// Include both `price` (numeric) and `values[0].formula` (string) as AVA expects both.
		base.priceComponents = [
			{
				price: price,
				values: [{ formula: price.toString() }],
			},
		];
		base.unitTag = node.unitTag ?? "";
		// taxRate is now stored as AVA decimal (e.g. 0.19 = 19 %). No conversion needed.
		// useDifferentTaxRate tells AVA Cloud to use this position's own rate;
		// when false, AVA falls back to the project-level rate.
		const hasTaxRate = node.taxRate != null;
		base.taxRate = hasTaxRate ? node.taxRate : 0;
		base.useDifferentTaxRate = hasTaxRate;
		base.elements = [];
	}

	// NoteTextDto: only structural flags the editor explicitly tracks
	if (
		node.type === ELEMENT_TYPES.NOTE ||
		node.type === ELEMENT_TYPES.ADDITIONAL_TEXT ||
		node.type === ELEMENT_TYPES.EXECUTION_DESCRIPTION
	) {
		if (node.type === ELEMENT_TYPES.ADDITIONAL_TEXT) {
			// Preserve the original open/close intent stored in _ava; default to closing
			base.isOpeningText = node._ava?.isOpeningText ?? false;
			base.isClosingText = node._ava?.isClosingText ?? true;
		} else {
			base.isOpeningText = false;
			base.isClosingText = false;
		}
		if (node.type === ELEMENT_TYPES.EXECUTION_DESCRIPTION) {
			base.addendumNumber = EXEC_DESC_MARKER;
		}
		base.elements = [];
	}

	return base;
}

/**
 * Rebuild a full AVA ProjectDto from the editor's current `rows`.
 *
 * HOW INDICES STAY CORRECT:
 *   `rows` is the single source of truth for order. Every add/remove/move
 *   operation in the editor updates `rows` via boqTreeUtils (addNodeToTree,
 *   removeNodeFromTree, moveNodeInTree, etc.). When we call editorNodesToAva,
 *   we simply map `rows → elements[]` in traversal order – the resulting array
 *   index is exactly what the user intended. No separate index bookkeeping needed.
 *
 * @param {Array}  rows         - editor root nodes (from `rows` useState)
 * @param {Object} baseGaebData - original ProjectDto (for preserving all top-level
 *                                metadata: projectInformation, bidder, taxRate, etc.)
 * @returns {Object}            - updated ProjectDto ready for recalculateGaebData()
 */
export function editorNodesToAva(rows, baseGaebData, schemaOverride = null) {
	const avaElements = (rows ?? []).map(editorNodeToAvaElement);

	const baseSpec = baseGaebData?.serviceSpecifications?.[0] ?? {};

	const baseProjectInfo = baseGaebData?.projectInformation ?? {};

	return {
		...baseGaebData,
		// Ensure AVA Cloud always has a schema in projectInformation - this is the
		// canonical location AVA reads from (matches every GAEB-imported file).
		// Prefer the explicitly passed schemaOverride (user-edited schema), then
		// the schema already in the payload, then our GAEB default.
		projectInformation: {
			...baseProjectInfo,
			itemNumberSchema:
				schemaOverride ??
				baseProjectInfo.itemNumberSchema ??
				DEFAULT_GAEB_ITEM_NUMBER_SCHEMA,
		},
		serviceSpecifications: [
			{
				...baseSpec,
				elements: avaElements,
			},
			// Preserve any additional service specifications (unlikely but safe)
			...(baseGaebData?.serviceSpecifications?.slice(1) ?? []),
		],
	};
}
